import { ExternalLink } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import type { QueueSummary, ReviewAnswer, ReviewGroupView } from "../../shared/review";
import { fmtInt, humanize } from "./ui";

/**
 * The evidence each queue's item view shows (queue.layout). Everything here is read by teammates,
 * some of them non-technical: plain labels, and hints marked as hints.
 */
type EvidenceProps = {
  queue: QueueSummary;
  group: ReviewGroupView;
  position: number;
  onSelect: (position: number) => void;
  canStacks: boolean;
};

export function ItemEvidence(props: EvidenceProps) {
  const item = props.group.items[props.position]!;
  return (
    <>
      <h2 className="review-q">{item.question}</h2>
      {props.queue.layout === "overruling" ? (
        <OverrulingEvidence {...props} />
      ) : props.queue.layout === "citation" ? (
        <CitationEvidence {...props} />
      ) : props.queue.layout === "added-citation" ? (
        <AddedCitationEvidence {...props} />
      ) : (
        <CaseGroupEvidence {...props} />
      )}
    </>
  );
}

/** One line naming a group, for the strip above the item and the owner's view. */
export function captionOf(layout: QueueSummary["layout"], fields: Record<string, string>) {
  if (layout === "overruling")
    return `${fields.overruling_name} (${fields.overruling_year}) and ${fields.overruled_name} (${fields.overruled_year})`;
  if (layout === "citation")
    return [fields.case_name, fields.court_name || fields.court_id, fields.decision_date]
      .filter(Boolean)
      .join(" · ");
  if (layout === "added-citation")
    return [fields.cite, fields.case_name, fields.court_id, fields.decision_date]
      .filter(Boolean)
      .join(" · ");
  return [fields.case_name, fields.docket_number && `No. ${fields.docket_number}`]
    .filter(Boolean)
    .join(" · ");
}

/** What the owner sees of one item beside the reviewers' answers. */
export function ItemSummary({
  layout,
  fields
}: {
  layout: QueueSummary["layout"];
  fields: Record<string, string>;
}) {
  const link = fields.evidence_url && (
    <a href={fields.evidence_url} target="_blank" rel="noopener noreferrer">
      CourtListener <ExternalLink aria-hidden="true" />
    </a>
  );
  if (layout === "overruling")
    return (
      <>
        {fields.overruling_name} ({fields.overruling_year}) → {fields.overruled_name} (
        {fields.overruled_year})
        <div className="review-owner-text">
          Computer: {fields.computer_reading}
          {fields.official_list === "yes" && " · on the official list"} {link}
        </div>
      </>
    );
  if (layout === "citation")
    return (
      <>
        <span className="mono">{fields.citation}</span> · {fields.decision_date}
        <div className="review-owner-text">
          {fields.problem} {link}
        </div>
      </>
    );
  if (layout === "added-citation") {
    const lookup = citationLookupUrl(fields.cite ?? "");
    return (
      <>
        <span className="mono">{fields.cite}</span> · {fields.case_name} · {fields.decision_date}
        <div className="review-owner-text">
          Already has: {fields.carried || "none"} · cited by {fields.citing_cases} later cases{" "}
          {link}{" "}
          {lookup && (
            <a href={lookup} target="_blank" rel="noopener noreferrer">
              Lookup <ExternalLink aria-hidden="true" />
            </a>
          )}
        </div>
      </>
    );
  }
  return (
    <>
      <span className="mono">{fields.source_id}</span> · {fields.decision_date} ·{" "}
      <span className="mono">{fields.docket_number}</span>
      <div className="review-owner-text">
        {fields.text_start} {link}
      </div>
    </>
  );
}

export function answerTone(answers: ReviewAnswer[], key: string) {
  const answer = answers.find((a) => a.key === key);
  return answer?.unsure ? "neutral" : answer?.changesData ? "warn" : "ok";
}

function AnswerChip({ answers, answer }: { answers: ReviewAnswer[]; answer?: string | undefined }) {
  if (!answer) return <span className="muted">–</span>;
  const label = answers.find((a) => a.key === answer)?.label ?? humanize(answer);
  return (
    <span className="status" data-tone={answerTone(answers, answer)}>
      <i aria-hidden="true" />
      {label} · you
    </span>
  );
}

function OutLink({
  href,
  children,
  hotkey
}: {
  href?: string | undefined;
  children: ReactNode;
  hotkey?: string | undefined;
}) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children} <ExternalLink aria-hidden="true" />
      {hotkey && <kbd className="key">{hotkey}</kbd>}
    </a>
  );
}

function useScrollToRow(position: number) {
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  useEffect(() => {
    rowRefs.current[position]?.scrollIntoView({ block: "nearest" });
  }, [position]);
  return rowRefs;
}

/* -------------------------------------------------------------------------- */
/* Duplicate case groups (IN-58)                                               */
/* -------------------------------------------------------------------------- */

/** Case numbers compare by their segments without leading zeros: 13-09-354-CR = 13-09-00354-cr. */
function docketKey(value: string) {
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((part) => part.replace(/^0+(?=\d)/, ""))
    .join("-");
}

/** The caption's distinctive party word: the last word of the first party, e.g. "Pereida". */
function partyWord(caseName: string) {
  const first = caseName.split(/\s+v\.?\s+|\s+vs\.?\s+/i)[0] ?? "";
  const words = first
    .replace(/^in re\s+/i, "")
    .split(/[^A-Za-z'-]+/)
    .filter((w) => w.length >= 3 && !/^(inc|llc|corp|co|ltd|the|and|of)$/i.test(w));
  return words.at(-1) ?? "";
}

function captionHints(fields: Record<string, string>, text: string) {
  const docket = docketKey(fields.docket_number ?? "");
  const tokens = text.match(/[A-Za-z0-9]+(?:[-:–][A-Za-z0-9]+)+/g) ?? [];
  const docketFound = Boolean(docket) && tokens.some((token) => docketKey(token) === docket);
  const party = partyWord(fields.case_name ?? "");
  const partyFound = Boolean(party) && new RegExp(`\\b${escape(party)}\\b`, "i").test(text);
  return { docketFound, party, partyFound };
}

function escape(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function CaptionMarked({ text, fields }: { text: string; fields: Record<string, string> }) {
  const docket = docketKey(fields.docket_number ?? "");
  const party = partyWord(fields.case_name ?? "");
  const pattern = /[A-Za-z0-9]+(?:[-:–][A-Za-z0-9]+)+|[A-Za-z'-]+/g;
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const word = match[0];
    const hit =
      (docket && /[-:–]/.test(word) && docketKey(word) === docket) ||
      (party && word.toLowerCase() === party.toLowerCase());
    if (!hit) continue;
    parts.push(text.slice(last, match.index), <mark key={match.index}>{word}</mark>);
    last = match.index + word.length;
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

function CaseGroupEvidence({ queue, group, position, onSelect, canStacks }: EvidenceProps) {
  const item = group.items[position]!;
  const first = group.items[0]!;
  const rowRefs = useScrollToRow(position);
  return (
    <>
      <p className="review-why">
        {captionOf("case-group", first.fields)}. {queue.why}
      </p>
      <p className="section-label">Records in this group</p>
      <div className="review-records">
        <table className="records fixed review-table">
          <colgroup>
            <col style={{ width: 32 }} />
            <col style={{ width: 82 }} />
            <col style={{ width: 92 }} />
            <col style={{ width: 128 }} />
            <col />
            <col style={{ width: 60 }} />
            <col style={{ width: 112 }} />
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>Record</th>
              <th>Decided</th>
              <th>Case number</th>
              <th>First lines</th>
              <th className="right">Words</th>
              <th>Your answer</th>
            </tr>
          </thead>
          <tbody>
            {group.items.map((row, index) => (
              <tr
                key={row.id}
                ref={(el) => {
                  rowRefs.current[index] = el;
                }}
                aria-current={index === position ? "true" : undefined}
                onClick={() => onSelect(index)}
              >
                <td className="num">{index + 1}</td>
                <td className="mono">{row.fields.source_id}</td>
                <td>{row.fields.decision_date}</td>
                <td className="mono">{row.fields.docket_number}</td>
                <td title={row.fields.text_start}>
                  {row.fields.blocked === "yes" ? (
                    <span className="muted">Hidden: this record's text is withheld</span>
                  ) : (
                    row.fields.text_start
                  )}
                </td>
                <td className="right num">{fmtInt(row.fields.text_words ?? "0")}</td>
                <td>
                  <AnswerChip answers={queue.answers} answer={row.myAnswer?.answer} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="review-foot-note">
        First lines come from the public court record on CourtListener. Highlights show the case
        number and name from the top, where they appear.
      </p>
      <div className="qp">
        {position !== 0 && <FirstLines title="Record 1 · first in group" fields={first.fields} />}
        <FirstLines
          title={`Record ${position + 1} · this one`}
          fields={item.fields}
          captionFields={first.fields}
          current
          links={
            <>
              <OutLink href={item.fields.evidence_url} hotkey="O">
                Full opinion on CourtListener
              </OutLink>
              {canStacks && (
                <a
                  href={`/?module=stacks&q=${encodeURIComponent(
                    [item.fields.case_name, item.fields.decision_date?.slice(0, 4)]
                      .filter(Boolean)
                      .join(" ")
                  )}`}
                  target="_blank"
                  rel="noopener"
                >
                  Search in Stacks <ExternalLink aria-hidden="true" />
                </a>
              )}
              {position !== 0 && (
                <OutLink href={first.fields.evidence_url}>Record 1 on CourtListener</OutLink>
              )}
            </>
          }
        />
      </div>
    </>
  );
}

function FirstLines({
  title,
  fields,
  captionFields,
  current = false,
  links
}: {
  title: string;
  fields: Record<string, string>;
  captionFields?: Record<string, string>;
  current?: boolean;
  links?: ReactNode;
}) {
  const blocked = fields.blocked === "yes";
  const text = fields.text_start ?? "";
  const reference = captionFields ?? fields;
  const hints = captionHints(reference, text);
  return (
    <section className="qp-side" data-side={current ? "this" : "first"} aria-label={title}>
      <div className="qp-k">
        <span>{title}</span>
        <span>{fields.decision_date}</span>
      </div>
      <div className="qp-meta">
        {fields.case_name} · No. {fields.docket_number} · {fields.court_id}
      </div>
      <p className="qp-t">
        {blocked ? (
          <span className="muted">This record's text is withheld. Open it on CourtListener.</span>
        ) : (
          <CaptionMarked text={text} fields={reference} />
        )}
        {!blocked && text.length >= 390 && " …"}
      </p>
      {!blocked && current && (
        <p className="qp-hints">
          Hints, not an answer: the case number from the top is{" "}
          <b>{hints.docketFound ? "in" : "not in"}</b> these first lines
          {hints.party && (
            <>
              ; “{hints.party}” is <b>{hints.partyFound ? "in" : "not in"}</b> them
            </>
          )}
          .
        </p>
      )}
      {links && <div className="qp-links">{links}</div>}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Supreme Court overrulings (IN-48)                                           */
/* -------------------------------------------------------------------------- */

/**
 * A passage with the producer's marks: ⟦…⟧ where the opinion mentions the earlier case (yellow),
 * ⟪…⟫ the words the computer relied on (blue). Marks may nest.
 */
export function MarkedText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  let mention = 0;
  let quote = 0;
  let buffer = "";
  const flush = (key: number) => {
    if (!buffer) return;
    let node: ReactNode = buffer;
    if (quote > 0) node = <span className="qp-quote">{node}</span>;
    if (mention > 0) node = <mark>{node}</mark>;
    parts.push(<span key={key}>{node}</span>);
    buffer = "";
  };
  [...text].forEach((char, index) => {
    if ("⟦⟧⟪⟫".includes(char)) {
      flush(index);
      if (char === "⟦") mention++;
      else if (char === "⟧") mention = Math.max(0, mention - 1);
      else if (char === "⟪") quote++;
      else quote = Math.max(0, quote - 1);
    } else buffer += char;
  });
  flush(text.length);
  return <>{parts}</>;
}

function CaseCard({
  title,
  name,
  year,
  cite,
  url,
  hotkey,
  current
}: {
  title: string;
  name?: string | undefined;
  year?: string | undefined;
  cite?: string | undefined;
  url?: string | undefined;
  hotkey?: string;
  current?: boolean;
}) {
  return (
    <section className="qp-side" data-side={current ? "this" : "first"} aria-label={title}>
      <div className="qp-k">
        <span>{title}</span>
        <span>{year}</span>
      </div>
      <p className="qp-t review-case-name">{name}</p>
      <div className="qp-meta mono">{cite}</div>
      <div className="qp-links">
        <OutLink href={url} hotkey={hotkey}>
          Full opinion on CourtListener
        </OutLink>
      </div>
    </section>
  );
}

function OverrulingEvidence({ queue, group, position }: EvidenceProps) {
  const f = group.items[position]!.fields;
  const blocked = f.blocked === "yes";
  const passages = ["passage_1", "passage_2", "passage_3", "passage_4"]
    .map((name) => f[name] ?? "")
    .filter(Boolean);
  return (
    <>
      <p className="review-why">{queue.why}</p>
      <div className="qp">
        <CaseCard
          title="Later case: did it overrule?"
          name={f.overruling_name}
          year={f.overruling_year}
          cite={f.overruling_cite}
          url={f.evidence_url}
          hotkey="O"
          current
        />
        <CaseCard
          title="Earlier case: was it overruled?"
          name={f.overruled_name}
          year={f.overruled_year}
          cite={f.overruled_cite}
          url={f.overruled_url}
        />
      </div>
      <dl className="review-facts">
        <div>
          <dt>On the official list of overruled decisions</dt>
          <dd>
            {f.official_list === "yes"
              ? "Yes. The Library of Congress's Constitution Annotated lists this pair."
              : "No. The Constitution Annotated doesn't list this pair. It can still be an overruling."}
          </dd>
        </div>
        <div>
          <dt>The computer's reading (a hint, not an answer)</dt>
          <dd>
            <b>{f.computer_reading || "–"}</b>
            {f.computer_reason && <span className="muted"> · {f.computer_reason}</span>}
          </dd>
        </div>
      </dl>
      <p className="section-label">
        From the later case's opinion
        {f.text_source && <span className="muted"> · {f.text_source}</span>}
      </p>
      {blocked ? (
        <p className="muted">This opinion's text is withheld. Open it on CourtListener.</p>
      ) : passages.length === 0 ? (
        <p className="muted">
          No passage was found that mentions the earlier case. Read the opinion on CourtListener
          (press O).
        </p>
      ) : (
        <div className="review-passages">
          {passages.map((text, index) => (
            <p className="qp-t review-passage" key={index}>
              <MarkedText text={text} />
            </p>
          ))}
        </div>
      )}
      <p className="review-foot-note">
        <mark>Yellow</mark>: the opinion mentions the earlier case.{" "}
        <span className="qp-quote">Blue</span>: the words the computer relied on. Passages are the
        paragraphs the computer read most closely; the whole opinion is on CourtListener.
      </p>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Citations that don't fit (IN-9)                                             */
/* -------------------------------------------------------------------------- */

function CitationEvidence({ queue, group, position, onSelect }: EvidenceProps) {
  const item = group.items[position]!;
  const f = item.fields;
  const rowRefs = useScrollToRow(position);
  return (
    <>
      <p className="review-why">{queue.why}</p>
      <div className="qp">
        <section className="qp-side" data-side="this" aria-label="The case">
          <div className="qp-k">
            <span>The case</span>
            <span>Decided {f.decision_date}</span>
          </div>
          <p className="qp-t review-case-name">
            {f.blocked === "yes" ? <span className="muted">Name withheld</span> : f.case_name}
          </p>
          <div className="qp-meta">{f.court_name || f.court_id}</div>
          <div className="qp-links">
            <OutLink href={f.evidence_url} hotkey="O">
              The case on CourtListener
            </OutLink>
          </div>
        </section>
        <section className="qp-side" aria-label="The citation">
          <div className="qp-k">
            <span>The citation</span>
          </div>
          <p className="qp-t mono">{f.citation}</p>
          <p className="qp-hints">{f.problem}</p>
        </section>
      </div>
      <dl className="review-facts">
        <div>
          <dt>The case's other citations</dt>
          <dd>{f.other_citations || <span className="muted">None</span>}</dd>
        </div>
        {f.fits_elsewhere && (
          <div>
            <dt>The same citation is also on</dt>
            <dd>{f.fits_elsewhere}</dd>
          </div>
        )}
        <div>
          <dt>Hint (the computer's guess, not an answer)</dt>
          <dd>
            <b>{f.hint || "No clear sign either way"}</b>
            {f.details && <div className="muted review-small">{f.details}</div>}
          </dd>
        </div>
      </dl>
      {group.items.length > 1 && (
        <>
          <p className="section-label">This case's citations that don't fit</p>
          <div className="review-records">
            <table className="records fixed review-table">
              <colgroup>
                <col style={{ width: 32 }} />
                <col style={{ width: 170 }} />
                <col />
                <col style={{ width: 150 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Citation</th>
                  <th>What doesn't fit</th>
                  <th>Your answer</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map((row, index) => (
                  <tr
                    key={row.id}
                    ref={(el) => {
                      rowRefs.current[index] = el;
                    }}
                    aria-current={index === position ? "true" : undefined}
                    onClick={() => onSelect(index)}
                  >
                    <td className="num">{index + 1}</td>
                    <td className="mono">{row.fields.citation}</td>
                    <td title={row.fields.problem}>{row.fields.problem}</td>
                    <td>
                      <AnswerChip answers={queue.answers} answer={row.myAnswer?.answer} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Citations added to a case: inferred (IN-57) and proposed (IN-51)            */
/* -------------------------------------------------------------------------- */

/**
 * CourtListener's citation lookup for "<volume> <reporter> <page>": it shows whichever case
 * CourtListener files at that address, or none.
 */
export function citationLookupUrl(cite: string) {
  const match = /^(\S+) (.+) (\S+)$/.exec(cite.trim());
  if (!match) return undefined;
  const [, volume, reporter, page] = match;
  return `https://www.courtlistener.com/c/${encodeURIComponent(reporter!)}/${encodeURIComponent(volume!)}/${encodeURIComponent(page!)}/`;
}

const routeLabels: Record<string, string> = {
  anchored: "A later case prints it beside a citation the case already has",
  name: "Three or more later cases print it with the case's name, court and year"
};

/** The passage with every mention of the citation marked. */
function CiteMarked({ text, cite }: { text: string; cite: string }) {
  if (!cite) return <>{text}</>;
  const parts = text.split(cite);
  return (
    <>
      {parts.map((part, index) => (
        <span key={index}>
          {index > 0 && <mark>{cite}</mark>}
          {part}
        </span>
      ))}
    </>
  );
}

function AddedCitationEvidence({ queue, group, position }: EvidenceProps) {
  const f = group.items[position]!.fields;
  const blocked = f.blocked === "yes";
  const lookup = citationLookupUrl(f.cite ?? "");
  const passages = [1, 2, 3, 4]
    .map((n) => ({
      text: f[`passage_${n}`] ?? "",
      url: f[`citing_url_${n}`] ?? "",
      name: f[`citing_case_${n}`] ?? ""
    }))
    .filter((p) => p.text || p.url);
  return (
    <>
      <p className="review-why">{queue.why}</p>
      <div className="qp">
        <section className="qp-side" data-side="this" aria-label="The case">
          <div className="qp-k">
            <span>The case</span>
            <span>Decided {f.decision_date}</span>
          </div>
          <p className="qp-t review-case-name">{f.case_name}</p>
          <div className="qp-meta">
            {[f.court_id, f.docket_number && `No. ${f.docket_number}`].filter(Boolean).join(" · ")}
          </div>
          <div className="qp-links">
            <OutLink href={f.evidence_url} hotkey="O">
              The case on CourtListener
            </OutLink>
          </div>
        </section>
        <section className="qp-side" aria-label="The citation">
          <div className="qp-k">
            <span>The citation</span>
          </div>
          <p className="qp-t mono">{f.cite}</p>
          <p className="qp-hints">
            {fmtInt(Number(f.citing_cases) || 0)} later{" "}
            {f.citing_cases === "1" ? "case cites" : "cases cite"} the case at this address.
          </p>
          <div className="qp-links">
            <OutLink href={lookup} hotkey="L">
              Look the citation up on CourtListener
            </OutLink>
          </div>
        </section>
      </div>
      <dl className="review-facts">
        <div>
          <dt>Citations the case already has</dt>
          <dd>{f.carried || <span className="muted">None</span>}</dd>
        </div>
        <div>
          <dt>The name the later cases print</dt>
          <dd>{f.cited_as || <span className="muted">–</span>}</dd>
        </div>
        {f.route !== undefined && (
          <div>
            <dt>How it was found</dt>
            <dd>{routeLabels[f.route] ?? f.route}</dd>
          </div>
        )}
        {f.proposal !== undefined && (
          <div>
            <dt>Why it's proposed</dt>
            <dd>{f.proposal || <span className="muted">–</span>}</dd>
          </div>
        )}
      </dl>
      <p className="section-label">From the later cases (a hint, not a source)</p>
      {blocked ? (
        <p className="muted">
          These passages are withheld. Open the citing cases on CourtListener.
        </p>
      ) : passages.length === 0 ? (
        <p className="muted">
          No passage was kept. Look the citation up on CourtListener (press L).
        </p>
      ) : (
        <div className="review-passages">
          {passages.map((p, index) => (
            <div key={index}>
              <div className="qp-links">
                {p.name && <span>{p.name}</span>}
                <OutLink href={p.url}>
                  {p.name ? "On CourtListener" : `Citing case ${index + 1}`}
                </OutLink>
              </div>
              {p.text && (
                <p className="qp-t review-passage">
                  <CiteMarked text={p.text} cite={f.cite ?? ""} />
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="review-foot-note">
        <mark>Yellow</mark>: the citation in the later case's words. Check it in a source of its own
        before you answer.
      </p>
    </>
  );
}
